// ============================================================================
// BP-09-04: Mentor API Client
// BrightPath Community - Peer Mentoring API
// ============================================================================

import apiClient from '@/core/lib/api-client';
import {
  Mentor,
  MentorListItem,
  MentorsResponse,
  MentorMatch,
  MentorMatchesResponse,
  MentorStatsResponse,
  MentorSearchParams,
  MentorApplicationInput,
  UpdateMentorProfileInput,
  MatchRequestInput,
  MatchFeedbackInput,
} from '../types';

const MENTORS_BASE = '/mentors';

// =============================================================================
// Mentor API
// =============================================================================

export const mentorApi = {
  /**
   * Get list of mentors
   */
  async getMentors(params?: MentorSearchParams): Promise<MentorsResponse> {
    const response = await apiClient.get<MentorsResponse>(MENTORS_BASE, { params });
    return response.data;
  },

  /**
   * Get mentor by ID
   */
  async getMentor(mentorId: string): Promise<Mentor> {
    const response = await apiClient.get<Mentor>(`${MENTORS_BASE}/${mentorId}`);
    return response.data;
  },

  /**
   * Get mentor statistics
   */
  async getMentorStats(): Promise<MentorStatsResponse> {
    const response = await apiClient.get<MentorStatsResponse>(`${MENTORS_BASE}/stats`);
    return response.data;
  },

  // ===========================================================================
  // Mentor Application & Profile
  // ===========================================================================

  /**
   * Apply to become a mentor
   */
  async applyToBecomeMentor(data: MentorApplicationInput): Promise<Mentor> {
    const response = await apiClient.post<Mentor>(`${MENTORS_BASE}/apply`, data);
    return response.data;
  },

  /**
   * Get current user's mentor profile
   */
  async getMyMentorProfile(): Promise<Mentor | null> {
    try {
      const response = await apiClient.get<Mentor>(`${MENTORS_BASE}/me`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Update mentor profile
   */
  async updateMentorProfile(data: UpdateMentorProfileInput): Promise<Mentor> {
    const response = await apiClient.patch<Mentor>(`${MENTORS_BASE}/me`, data);
    return response.data;
  },

  /**
   * Toggle accepting new matches
   */
  async toggleAcceptingMatches(accepting: boolean): Promise<Mentor> {
    const response = await apiClient.patch<Mentor>(`${MENTORS_BASE}/me`, {
      is_accepting_matches: accepting,
    });
    return response.data;
  },

  // ===========================================================================
  // Mentor Matching
  // ===========================================================================

  /**
   * Request a mentor match
   */
  async requestMatch(data: MatchRequestInput): Promise<MentorMatch> {
    const response = await apiClient.post<MentorMatch>(
      `${MENTORS_BASE}/${data.mentor_id}/request-match`,
      { match_reason: data.match_reason }
    );
    return response.data;
  },

  /**
   * Get current user's matches (as mentor)
   */
  async getMyMentorMatches(): Promise<MentorMatch[]> {
    const response = await apiClient.get<MentorMatch[]>(`${MENTORS_BASE}/me/matches`);
    return response.data;
  },

  /**
   * Get current user's mentor matches (as mentee)
   */
  async getMyMenteeMatches(): Promise<MentorMatch[]> {
    const response = await apiClient.get<MentorMatch[]>('/users/me/mentor-matches');
    return response.data;
  },

  /**
   * Get all matches for current user (both as mentor and mentee)
   */
  async getAllMyMatches(): Promise<MentorMatchesResponse> {
    const [asMentor, asMentee] = await Promise.all([
      this.getMyMentorMatches().catch(() => []),
      this.getMyMenteeMatches().catch(() => []),
    ]);
    return {
      as_mentor: asMentor,
      as_mentee: asMentee,
    };
  },

  /**
   * Accept a match request (as mentor)
   */
  async acceptMatch(matchId: string): Promise<MentorMatch> {
    const response = await apiClient.patch<MentorMatch>(
      `/mentor-matches/${matchId}`,
      { status: 'accepted' }
    );
    return response.data;
  },

  /**
   * Decline a match request (as mentor)
   */
  async declineMatch(matchId: string, reason?: string): Promise<MentorMatch> {
    const response = await apiClient.patch<MentorMatch>(
      `/mentor-matches/${matchId}`,
      { status: 'declined', mentor_notes: reason }
    );
    return response.data;
  },

  /**
   * Start an active mentorship
   */
  async startMentorship(matchId: string): Promise<MentorMatch> {
    const response = await apiClient.patch<MentorMatch>(
      `/mentor-matches/${matchId}`,
      { status: 'active' }
    );
    return response.data;
  },

  /**
   * Complete a mentorship
   */
  async completeMentorship(matchId: string): Promise<MentorMatch> {
    const response = await apiClient.post<MentorMatch>(
      `/mentor-matches/${matchId}/complete`
    );
    return response.data;
  },

  /**
   * Cancel a match
   */
  async cancelMatch(matchId: string, reason?: string): Promise<MentorMatch> {
    const response = await apiClient.patch<MentorMatch>(
      `/mentor-matches/${matchId}`,
      { status: 'cancelled', mentor_notes: reason }
    );
    return response.data;
  },

  /**
   * Leave feedback for a completed match
   */
  async leaveFeedback(matchId: string, data: MatchFeedbackInput): Promise<MentorMatch> {
    const response = await apiClient.post<MentorMatch>(
      `/mentor-matches/${matchId}/feedback`,
      data
    );
    return response.data;
  },

  // ===========================================================================
  // Messaging
  // ===========================================================================

  /**
   * Send a message within a mentorship
   */
  async sendMessage(matchId: string, message: string): Promise<void> {
    await apiClient.post(`/mentor-matches/${matchId}/message`, { message });
  },
};

export default mentorApi;
